/**
 * Shared <ContextMenu> primitive.
 *
 * One implementation, every right-click menu in every Quotech tenant
 * SPA. Render-prop style — the consumer owns ``open`` / ``position``
 * state and supplies a declarative ``ContextMenuItem[]``. The
 * primitive owns positioning, click-outside, Escape, focus trap,
 * keyboard nav, and submenu fly-out.
 *
 * Usage
 * -----
 * ```tsx
 * const [menu, setMenu] = useState<{ open: boolean; pos: { x:number; y:number }}>({ open: false, pos: { x:0, y:0 } })
 *
 * <tr
 *   onContextMenu={(e) => {
 *     e.preventDefault()
 *     setMenu({ open: true, pos: { x: e.clientX, y: e.clientY } })
 *   }}
 * >...</tr>
 *
 * <ContextMenu
 *   open={menu.open}
 *   position={menu.pos}
 *   onClose={() => setMenu(s => ({ ...s, open: false }))}
 *   items={buildClientRowMenu(client, { onOpenInTab, onDelete, … })}
 * />
 * ```
 *
 * Companion helper: ``useContextMenu()`` packs the open/position state
 * into a single hook for the common case.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { ChevronRight } from 'lucide-react'
import { cn } from '../lib/utils'
import type { ContextMenuItem, ContextMenuPosition, ContextMenuProps } from './types'

interface MenuPaneProps {
  items: ContextMenuItem[]
  width: number
  onSelect: (item: ContextMenuItem, e: React.MouseEvent | React.KeyboardEvent) => void
  onCloseAll: () => void
  onCloseToParent?: () => void
  autoFocus?: boolean
}

function MenuPane({
  items,
  width,
  onSelect,
  onCloseAll,
  onCloseToParent,
  autoFocus = false,
}: MenuPaneProps) {
  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null)
  const [focusSubmenuId, setFocusSubmenuId] = useState<string | null>(null)
  const itemRefs = useRef(new Map<string, HTMLButtonElement>())
  const renderable = useMemo(() => items.filter(Boolean), [items])
  const enabledItems = useMemo(
    () => renderable.filter((item) => item.kind !== 'separator' && item.kind !== 'header' && !item.disabled),
    [renderable],
  )

  const focusItem = useCallback((item: ContextMenuItem | undefined) => {
    if (item) itemRefs.current.get(item.id)?.focus()
  }, [])
  const focusFirst = useCallback(() => focusItem(enabledItems[0]), [enabledItems, focusItem])

  useLayoutEffect(() => {
    if (autoFocus) focusFirst()
  }, [autoFocus, focusFirst])

  const closeSubmenu = useCallback(() => {
    // Reused from: packages/ui/src/contextMenu/ContextMenu.tsx (MenuPane onCloseToParent)
    setOpenSubmenuId(null)
    setFocusSubmenuId(null)
  }, [])

  const focusItemClosingSubmenu = useCallback((item: ContextMenuItem | undefined) => {
    closeSubmenu()
    focusItem(item)
  }, [closeSubmenu, focusItem])

  const moveFocus = useCallback((currentId: string, direction: 1 | -1) => {
    const currentIndex = enabledItems.findIndex((item) => item.id === currentId)
    if (currentIndex === -1 || enabledItems.length === 0) return
    focusItemClosingSubmenu(enabledItems[(currentIndex + direction + enabledItems.length) % enabledItems.length])
  }, [enabledItems, focusItemClosingSubmenu])

  const openSubmenu = useCallback((item: ContextMenuItem, focusChild: boolean) => {
    if (!item.submenu?.length || item.disabled) return
    setOpenSubmenuId(item.id)
    if (focusChild) setFocusSubmenuId(item.id)
  }, [])

  return (
    <div
      role="menu"
      aria-orientation="vertical"
      className={cn(
        'rounded-md border border-border bg-popover py-1 shadow-xl',
        'text-popover-foreground',
      )}
      style={{ minWidth: width }}
    >
      {renderable.map((item) => {
        if (item.kind === 'separator') {
          return <div key={item.id} role="separator" className="my-1 h-px bg-border" onMouseEnter={closeSubmenu} />
        }
        if (item.kind === 'header') {
          return (
            <div
              key={item.id}
              className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
              onMouseEnter={closeSubmenu}
            >
              {item.label}
            </div>
          )
        }
        const Icon = item.icon
        const hasSubmenu = (item.submenu?.length ?? 0) > 0
        const isOpen = openSubmenuId === item.id
        const destructive = item.kind === 'destructive'
        return (
          <div
            key={item.id}
            className="relative"
            onMouseEnter={() => {
              if (hasSubmenu && !item.disabled) openSubmenu(item, false)
              else closeSubmenu()
            }}
          >
            <button
              type="button"
              role="menuitem"
              disabled={item.disabled}
              aria-disabled={item.disabled || undefined}
              aria-haspopup={hasSubmenu ? 'menu' : undefined}
              aria-expanded={hasSubmenu ? isOpen : undefined}
              ref={(element) => {
                if (element) itemRefs.current.set(item.id, element)
                else itemRefs.current.delete(item.id)
              }}
              onClick={(e) => {
                if (item.disabled) return
                if (hasSubmenu) {
                  openSubmenu(item, false)
                  return
                }
                item.onSelect?.(e)
                onSelect(item, e)
              }}
              onKeyDown={(e) => {
                switch (e.key) {
                  case 'ArrowDown':
                    e.preventDefault()
                    moveFocus(item.id, 1)
                    break
                  case 'ArrowUp':
                    e.preventDefault()
                    moveFocus(item.id, -1)
                    break
                  case 'Home':
                    e.preventDefault()
                    focusItemClosingSubmenu(enabledItems[0])
                    break
                  case 'End':
                    e.preventDefault()
                    focusItemClosingSubmenu(enabledItems.at(-1))
                    break
                  case 'ArrowRight':
                    if (hasSubmenu) {
                      e.preventDefault()
                      openSubmenu(item, true)
                    }
                    break
                  case 'ArrowLeft':
                    if (onCloseToParent) {
                      e.preventDefault()
                      onCloseToParent()
                    }
                    break
                  case 'Enter':
                  case ' ':
                    e.preventDefault()
                    if (hasSubmenu) openSubmenu(item, true)
                    else if (!item.disabled) {
                      item.onSelect?.(e)
                      onSelect(item, e)
                    }
                    break
                  case 'Escape':
                    e.preventDefault()
                    e.stopPropagation()
                    onCloseAll()
                    break
                  default:
                    break
                }
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] transition-colors',
                item.disabled && 'cursor-not-allowed opacity-40',
                !item.disabled && (
                  destructive
                    ? 'hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400'
                    : 'hover:bg-muted'
                ),
              )}
            >
              {Icon ? (
                <Icon className={cn('h-3.5 w-3.5 shrink-0', destructive ? 'text-red-500' : 'text-muted-foreground')} />
              ) : (
                <span className="h-3.5 w-3.5 shrink-0" />
              )}
              <span className="flex-1 truncate">{item.label}</span>
              {item.hint && <span className="ml-2 text-[10px] text-muted-foreground">{item.hint}</span>}
              {hasSubmenu && <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />}
            </button>
            {hasSubmenu && isOpen && (
              <div className="absolute left-full top-0 ml-1 z-10">
                <MenuPane
                  items={item.submenu ?? []}
                  width={width}
                  onSelect={onSelect}
                  onCloseAll={onCloseAll}
                  autoFocus={focusSubmenuId === item.id}
                  onCloseToParent={() => {
                    closeSubmenu()
                    focusItem(item)
                  }}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function ContextMenu({
  open,
  position,
  items,
  onClose,
  header,
  width = 220,
  trigger,
}: ContextMenuProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [anchored, setAnchored] = useState<ContextMenuPosition>(position)
  const closeAndRestoreFocus = useCallback(() => {
    onClose()
    if (trigger?.isConnected) requestAnimationFrame(() => trigger.focus())
  }, [onClose, trigger])

  // Reposition so the menu always fits inside the viewport. Runs in
  // a layout effect so the user never sees a flash at the wrong spot.
  useLayoutEffect(() => {
    if (!open) return
    const el = wrapperRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    let { x, y } = position
    if (x + rect.width > vw - 8) x = Math.max(8, vw - rect.width - 8)
    if (y + rect.height > vh - 8) y = Math.max(8, vh - rect.height - 8)
    setAnchored({ x, y })
  }, [open, position])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        closeAndRestoreFocus()
      }
    }
    const onContextMenuEvt = (e: MouseEvent) => {
      // A second right-click should re-anchor (default browser handles
      // that already because the consumer's `onContextMenu` will
      // re-set position) — but a right-click on the underlying chrome
      // should close the menu first to avoid stacking.
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        closeAndRestoreFocus()
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAndRestoreFocus()
    }
    // NOTE: we intentionally do NOT auto-close on scroll. The menu is
    // position:fixed and captures all the entity data it needs at
    // open time, so the operator can scroll the underlying table to
    // peek at adjacent rows without losing their menu — much closer
    // to native OS context-menu behaviour. (Closing on scroll was
    // the v1 behaviour and operators consistently complained about
    // it on the Jobs Tracker.)
    window.addEventListener('mousedown', onPointerDown)
    window.addEventListener('contextmenu', onContextMenuEvt)
    window.addEventListener('keydown', onKey)
    window.addEventListener('resize', closeAndRestoreFocus)
    return () => {
      window.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('contextmenu', onContextMenuEvt)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', closeAndRestoreFocus)
    }
  }, [open, closeAndRestoreFocus])

  useLayoutEffect(() => {
    if (open) {
      wrapperRef.current
        ?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')
        ?.focus()
    }
  }, [open, items])

  const handleSelect = useCallback(
    (_item: ContextMenuItem) => {
      // Close on any leaf-item activation. Submenu items pre-handle
      // their own dispatch via the ``onSelect`` they're rendered with.
      closeAndRestoreFocus()
    },
    [closeAndRestoreFocus],
  )

  if (!open) return null

  return createPortal(
    <div
      ref={wrapperRef}
      style={{ position: 'fixed', top: anchored.y, left: anchored.x, zIndex: 9999 }}
    >
      {header && (
        <div className="mb-1 rounded-md border border-border bg-popover px-3 py-2 shadow-xl">
          {header}
        </div>
      )}
      <MenuPane items={items} width={width} onSelect={handleSelect} onCloseAll={closeAndRestoreFocus} />
    </div>,
    document.body,
  )
}

/**
 * Convenience hook — packs (open, position, openAt, close) into one
 * tuple so consumers can write:
 *
 *   const ctx = useContextMenu()
 *   <tr onContextMenu={(e) => ctx.openAt(e)}>…</tr>
 *   <ContextMenu {...ctx.props} items={…} />
 */
// The hook already lives beside the menu component. The line keeps the existing export.
// eslint-disable-next-line react-refresh/only-export-components
export function useContextMenu() {
  const [state, setState] = useState<{ open: boolean; position: ContextMenuPosition; trigger: HTMLElement | null }>({
    open: false,
    position: { x: 0, y: 0 },
    trigger: null,
  })

  const openAt = useCallback((e: React.MouseEvent | {
    clientX: number
    clientY: number
    currentTarget?: EventTarget | null
    preventDefault?: () => void
  }) => {
    if ('preventDefault' in e) e.preventDefault?.()
    const trigger = 'currentTarget' in e && e.currentTarget instanceof HTMLElement
      ? e.currentTarget
      : document.activeElement instanceof HTMLElement ? document.activeElement : null
    setState({ open: true, position: { x: e.clientX, y: e.clientY }, trigger })
  }, [])

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }))
  }, [])

  return {
    isOpen: state.open,
    position: state.position,
    trigger: state.trigger,
    openAt,
    close,
    /** Spread directly into <ContextMenu {...props.props} items={…} /> */
    props: {
      open: state.open,
      position: state.position,
      onClose: close,
      trigger: state.trigger,
    },
  }
}
